import React, { useState, useEffect, useCallback } from 'react';
import { supabase, formatDT, STATUS_STYLES } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const ACTIVE_STATUSES = ['Open', 'In Transit', 'Booked', 'Dispatched'];

function isRecentDelivered(delivered_at) {
  if (!delivered_at) return false;
  const d = new Date(delivered_at);
  const now = new Date();
  // 7 business days — approximate as 10 calendar days for simplicity
  const diff = (now - d) / (1000 * 60 * 60 * 24);
  return diff <= 10;
}

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES['Open'];
  return (
    <span className="pill" style={{ background: style.bg, color: style.color }}>
      {status}
    </span>
  );
}

export default function ReportsPage() {
  const [entities, setEntities] = useState([]);
  const [loads, setLoads] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [sendResult, setSendResult] = useState({});
  const [expanded, setExpanded] = useState({});
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);

    const { data: entityData } = await supabase
      .from('entities')
      .select('*, contacts(*)')
      .eq('status', 'active')
      .order('name');

    const { data: loadData } = await supabase
      .from('loads')
      .select('*')
      .order('pickup_dt', { ascending: true });

    // Group loads by entity
    const grouped = {};
    (entityData || []).forEach(e => {
      const entityLoads = (loadData || []).filter(l => {
        if (l.entity_id !== e.id) return false;
        if (l.excluded) return false;
        if (!e.show_open_loads && l.status === 'Open') return false;
        if (ACTIVE_STATUSES.includes(l.status)) return true;
        if (l.status === 'Delivered' && isRecentDelivered(l.delivered_at)) return true;
        return false;
      });
      grouped[e.id] = entityLoads;
    });

    setEntities(entityData || []);
    setLoads(grouped);
    // Expand all by default
    const exp = {};
    (entityData || []).forEach(e => exp[e.id] = true);
    setExpanded(exp);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function updateLoad(loadId, field, value) {
    setLoads(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(eid => {
        next[eid] = next[eid].map(l => l.id === loadId ? { ...l, [field]: value } : l);
      });
      return next;
    });
    await supabase.from('loads').update({ [field]: value }).eq('id', loadId);
  }

  async function sendReport(entity) {
    setSending(entity.id);
    setSendResult(prev => ({ ...prev, [entity.id]: null }));

    try {
      const entityLoads = loads[entity.id] || [];
      if (entityLoads.length === 0) {
        setSendResult(prev => ({ ...prev, [entity.id]: { error: 'No loads to send' } }));
        setSending(null);
        return;
      }

      const contacts = entity.contacts?.filter(c => c.active) || [];
      if (contacts.length === 0) {
        setSendResult(prev => ({ ...prev, [entity.id]: { error: 'No contacts configured' } }));
        setSending(null);
        return;
      }

      // Generate magic link
      const token = crypto.randomUUID();
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      // Upsert magic link for this entity
      await supabase.from('magic_links').upsert(
        { entity_id: entity.id, token, expires_at: expiresAt },
        { onConflict: 'entity_id' }
      );

      const magicUrl = `${window.location.origin}/view/${token}`;
      const html = buildEmailHTML(entity.name, entityLoads, magicUrl);
      const subject = `Load Status Update — ${entity.name} — ${fmtDate(new Date())}`;

      // Send via mailto (opens email client) or Supabase Edge Function
      // For now we use the mailto approach and log the send
      const emails = contacts.map(c => c.email);

      // Log the send
      await supabase.from('email_log').insert({
        entity_id:      entity.id,
        entity_name:    entity.name,
        sent_by:        user?.email || 'team@nimbleinc.net', // FALLBACK JUST IN CASE
        contact_emails: emails,
        load_count:     entityLoads.length,
        cadence:        entity.cadence,
      });

      // Open mailto
      const mailtoEmails = emails.join(',');
      const encodedSubject = encodeURIComponent(subject);
      window.open(`mailto:${mailtoEmails}?subject=${encodedSubject}&body=See%20attached%20HTML%20report`, '_blank');

      // Also copy HTML to clipboard for paste into email
      await navigator.clipboard.writeText(html);

      setSendResult(prev => ({ ...prev, [entity.id]: { success: true, count: entityLoads.length, emails } }));
    } catch (err) {
      setSendResult(prev => ({ ...prev, [entity.id]: { error: err.message } }));
    }

    setSending(null);
  }

  async function sendAll() {
    for (const entity of entities) {
      await sendReport(entity);
    }
  }

  if (loading) return <div style={{padding:40,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Review loads, make edits, then send</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={fetchData}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={sendAll}>Send All</button>
        </div>
      </div>

      {entities.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <h3>No customers configured</h3>
          <p>Add customers in the Customers tab first</p>
        </div>
      )}

      {entities.map(entity => {
        const entityLoads = loads[entity.id] || [];
        const result = sendResult[entity.id];
        const isExpanded = expanded[entity.id];

        return (
          <div className="card" key={entity.id} style={{marginBottom:20}}>
            <div className="card-header">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setExpanded(prev => ({ ...prev, [entity.id]: !prev[entity.id] }))}
                  style={{background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--navy)'}}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
                <div>
                  <span className="card-title">{entity.name}</span>
                  <span style={{marginLeft:10,fontSize:11,color:'var(--text-secondary)'}}>
                    {entityLoads.length} load{entityLoads.length !== 1 ? 's' : ''} · {entity.cadence}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {entity.contacts?.filter(c=>c.active).map(c => (
                  <span key={c.id} className="badge badge-blue" style={{fontSize:10}}>{c.email}</span>
                ))}
                <button
                  className="btn btn-sky btn-sm"
                  onClick={() => sendReport(entity)}
                  disabled={sending === entity.id || entityLoads.length === 0}
                >
                  {sending === entity.id ? <span className="spinner" style={{width:12,height:12}} /> : '✉'} Send
                </button>
              </div>
            </div>

            {result && (
              <div className={`alert alert-${result.success ? 'success' : 'error'}`} style={{marginBottom:12}}>
                {result.success
                  ? `✓ Sent ${result.count} loads. HTML copied to clipboard — paste into your email client.`
                  : `Error: ${result.error}`}
              </div>
            )}

            {isExpanded && (
              entityLoads.length === 0 ? (
                <div style={{padding:'20px 0', textAlign:'center', color:'var(--text-secondary)', fontSize:13}}>
                  No active loads for this customer
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Pro #</th>
                        <th>Status</th>
                        <th>Origin</th>
                        <th>Destination</th>
                        <th>Pickup</th>
                        <th>Delivery</th>
                        <th>Equipment</th>
                        <th>Description</th>
                        <th>Unit #</th>
                        <th>PO #</th>
                        <th style={{textAlign:'center'}}>⚑</th>
                        <th style={{textAlign:'center'}}>Exclude</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entityLoads.map(load => (
                        <tr key={load.id} style={load.excluded ? {opacity:0.4, textDecoration:'line-through'} : load.priority ? {background:'#fff8e1'} : {}}>
                          <td className="font-mono fw-600 text-navy">{load.load_num}</td>
                          <td><StatusPill status={load.status} /></td>
                          <td>{load.origin_city}, {load.origin_state}</td>
                          <td>{load.dest_city}, {load.dest_state}</td>
                          <td style={{fontSize:12}}>{formatDT(load.pickup_dt)}</td>
                          <td style={{fontSize:12}}>{formatDT(load.delivery_dt)}</td>
                          <td style={{fontSize:12}}>{load.equipment}</td>
                          <td>
                            <input
                              className="inline-edit"
                              defaultValue={load.description || ''}
                              placeholder="—"
                              onBlur={e => updateLoad(load.id, 'description', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="inline-edit"
                              defaultValue={load.unit_num || ''}
                              placeholder="—"
                              style={{width:80}}
                              onBlur={e => updateLoad(load.id, 'unit_num', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="inline-edit"
                              defaultValue={load.po_num || ''}
                              placeholder="—"
                              style={{width:100}}
                              onBlur={e => updateLoad(load.id, 'po_num', e.target.value)}
                            />
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={!!load.priority}
                              onChange={e => updateLoad(load.id, 'priority', e.target.checked)}
                            />
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={!!load.excluded}
                              onChange={e => updateLoad(load.id, 'excluded', e.target.checked)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

function fmtDate(d) {
  return `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
}

function buildEmailHTML(customerName, loads, magicUrl) {
  const today = fmtDate(new Date());
  const rows = loads.map(load => {
    const style = STATUS_STYLES[load.status] || STATUS_STYLES['Open'];
    const pillStyle = `display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600;background:${style.bg};color:${style.color};`;
    const rowBg = load.priority ? 'background:#fff8e1;' : '';
    const flag = load.priority ? '⚑' : '';
    return `
      <tr style="${rowBg}">
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-weight:600;color:#000F9F;font-family:monospace;">${load.load_num}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;"><span style="${pillStyle}">${load.status}</span></td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;">${load.origin_city}, ${load.origin_state}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;">${load.dest_city}, ${load.dest_state}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${formatDT(load.pickup_dt)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${formatDT(load.delivery_dt)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${load.equipment || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${load.description || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${load.unit_num || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;font-size:12px;">${load.po_num || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e8ecf5;text-align:center;color:#c0392b;">${flag}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500&family=Syne:wght@700&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#f0f2f8;font-family:'DM Sans',Arial,sans-serif;">
<div style="max-width:900px;margin:20px auto;background:#fff;border-radius:10px;overflow:hidden;border:1px solid #dde2f0;">
  <div style="background:#000F9F;padding:20px 28px;display:flex;justify-content:space-between;align-items:center;">
    <div>
      <img src="https://YOUR-NETLIFY-URL/nimble-flag-dark.png" alt="Nimble" style="height:44px;display:block;" />
    </div>
    <div style="text-align:right;">
      <div style="color:#fff;font-size:14px;font-weight:600;">Load Status Update</div>
      <div style="color:#6BC4E8;font-size:11px;">${customerName} &nbsp;|&nbsp; ${today}</div>
    </div>
  </div>
  <div style="background:#f8f9fc;border-bottom:1px solid #e0e4f0;padding:12px 28px;">
    <p style="margin:0;font-size:13px;color:#555;">Current status of your active shipments. Reply to this email with any questions.</p>
  </div>
  <table style="width:100%;border-collapse:collapse;font-size:13px;">
    <thead>
      <tr style="background:#000F9F;color:#fff;">
        <th style="padding:10px;text-align:left;font-size:11px;">Pro #</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Status</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Origin</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Destination</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Pickup</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Delivery</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Equipment</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Description</th>
        <th style="padding:10px;text-align:left;font-size:11px;">Unit #</th>
        <th style="padding:10px;text-align:left;font-size:11px;">PO #</th>
        <th style="padding:10px;text-align:center;font-size:11px;">⚑</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="background:#f8f9fc;border-top:1px solid #e0e4f0;padding:16px 28px;text-align:center;">
    <a href="${magicUrl}" style="display:inline-block;background:#000F9F;color:#fff;padding:10px 24px;border-radius:7px;text-decoration:none;font-size:13px;font-weight:600;">View Load History →</a>
    <p style="margin:8px 0 0;font-size:11px;color:#999;">Link valid for 7 days · ${customerName}</p>
  </div>
  <div style="background:#000F9F;padding:14px 28px;display:flex;justify-content:space-between;align-items:center;">
    <span style="color:#6BC4E8;font-size:11px;">Questions? Reply to this email or call your Nimble representative.</span>
    <span style="color:#fff;font-size:11px;">nimbleinc.net</span>
  </div>
</div>
</body></html>`;
}
